import React, { useEffect, useState, useCallback } from "react";
import * as Yup from 'yup';
import { connect, useSelector, useDispatch, shallowEqual } from "react-redux";
import { useFormik } from "formik";
import { injectIntl } from "react-intl";
import * as auth from "app/modules/Auth/_redux/authRedux";
import Logo from "../../../../assets/images/login-logo.png";
import SingleInput from "./SingleOTPInput";
import { isEmpty } from 'lodash';

import "./Auth.css";

const otpLength = 4;

const verifyCodeSchema = Yup.object().shape({
    code1: Yup.string().trim().required(),
    code2: Yup.string().trim().required(),
    code3: Yup.string().trim().required(),
    code4: Yup.string().trim().required(),
});

const confirmCodeInitialValues = {
    code1: "",
    code2: "",
    code3: "",
    code4: ""
};

function OTP(props) {
    
    useEffect(() => {
        try {
            document.title = `ethizo`;
            if (window?.NUSA_closeVuiForm) {
                window?.NUSA_closeVuiForm();
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    const dispatch = useDispatch();

    const [activeInput, setActiveInput] = useState(1);

    const { user,counter } = useSelector(
        ({ auth }) => ({
            user: auth.user,
            counter: auth.counter,
        }),
        shallowEqual
    );

    const formik = useFormik({
        initialValues: confirmCodeInitialValues,
        validationSchema: verifyCodeSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values, { setStatus }) => {
            try {
                const { code1, code2, code3, code4 } = values;
                const code = `${code1}${code2}${code3}${code4}`;
                dispatch(props.setCounter(counter+1))
                if (code === user?.pincode) {
                    props.handleReset();
                } else {
                    setStatus('Your PIN is not valid');
                }

            } catch (err) {
                setStatus(err);
            }
        },
    });

    // Focus `inputIndex` input
    const focusInput = useCallback(
        (inputIndex) => {
            const selectedIndex = Math.max(Math.min(otpLength, inputIndex), 0);
            setActiveInput(selectedIndex);
        },
        [otpLength]
    );

    // Handle onFocus input
    const handleOnFocus = useCallback(
        (index) => () => {
            focusInput(index);
        },
        [focusInput]
    );


    const focusPrevInput = useCallback(() => {
        if (activeInput > 1) {
            focusInput(activeInput - 1);
        }

    }, [activeInput, focusInput]);

    const focusNextInput = useCallback(() => {
        focusInput(activeInput + 1);
    }, [activeInput, focusInput]);

    useEffect(() => {
        if ((formik.status && formik.status !== '') && isEmpty(formik.errors)) {
            setTimeout(() => {
                formik.setStatus('');
                formik.resetForm();
                setActiveInput(1);
            }, 3000);
        }
    }, [formik.status, formik.errors]);

    useEffect(() => {
        if (props?.isIdle && formik && !formik.status && isEmpty(formik.errors)) {
            formik.setStatus('');
            formik.resetForm();
        }
    }, [props?.isIdle]);

    // Handle onChange value for each input
    const handleOnChange = (value, index) => {
        if (formik.status) {
            formik.setStatus('');
        }
        if (isNaN(value)) return
        formik.setFieldValue(`code${index}`, value);
        if (index !== 4) {
            focusNextInput();
        }
    };

    // Hanlde onBlur input
    const onBlur = useCallback(() => {
        setActiveInput(-1);
    }, []);

    // Handle onKeyDown input
    const handleOnKeyDown = useCallback(
        (e) => {
            const pressedKey = e.key;
            if (formik.status) {
                formik.setStatus('');
            }
            switch (pressedKey) {
                case "Backspace":
                case "Delete": {
                    e.preventDefault();
                    if (formik.values[`code${activeInput}`]) {
                        formik.setFieldValue(`code${activeInput}`, "");
                    } else {
                        focusPrevInput();
                    }
                    focusPrevInput();
                    break;
                }
                case "ArrowLeft": {
                    e.preventDefault();
                    focusPrevInput();
                    break;
                }
                case "ArrowRight": {
                    e.preventDefault();
                    focusNextInput();
                    break;
                }
                default: {
                    if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
                        e.preventDefault();
                    }

                    break;
                }
            }
        },
        [activeInput, focusNextInput, focusPrevInput, formik.values]
    );

    // Handle onPaste input
    const handleOnPaste = useCallback(
        (e) => {
            e.preventDefault();
            const pastedData = e.clipboardData
                .getData("text/plain")
                .trim()
                .slice(0, (otpLength + 1) - activeInput)
                .split("");
            if (pastedData) {
                let nextFocusIndex = 1;
                Object?.values(formik?.values)?.forEach((val, index) => {
                    if ((index + 1) >= activeInput) {
                        const changedValue = pastedData.shift() || val;
                        if (!isNaN(changedValue)) {
                            nextFocusIndex = (index + 1);
                            formik.setFieldValue(`code${nextFocusIndex}`, changedValue);
                        }
                    }
                });
                setActiveInput(Math.min(nextFocusIndex, otpLength));
            }
        },
        [activeInput, otpLength, formik.values]
    );

    useEffect(() => {
        if (formik.values.code1 !== '' && formik.values.code2 !== '' && formik.values.code3 !== '' && formik.values.code4 !== '') {
            setTimeout(() => {
                formik.handleSubmit();
            }, 100)
        }

    }, [formik.values.code1, formik.values.code2, formik.values.code3, formik.values.code4])
    return (
        <div className="login-bg h-screen flex items-center justify-center z-[100]">
            <div className="login-box rounded-3xl   p-12 w-[650px]">
                <form onSubmit={formik.handleSubmit}>
                    <div className="flex justify-center">
                        <div>
                            <h3 className="text-lg mb-2 font-normal text-black-39">Welcome to</h3>
                            <img src={Logo} alt="" className="w-[237px]" />
                            <p className="mt-2 text-right text-vColor font-light">V 2.0</p>
                        </div>
                    </div>
                    <div className="flex spacing-y-4 content-center flex-col">
                        <h4 className="text-3xl text-center text-black font-light mb-4">Please Enter 4 Digits PIN</h4>
                        <div className="self-center flex flex-row justify-between w-4/5 mb-4">
                            {
                                Array(otpLength)
                                    .fill("")
                                    .map((_, index) => {
                                        const curIndex = index + 1;
                                        return <SingleInput
                                            key={curIndex.toString()}
                                            type="password"
                                            name={`code${curIndex}`}
                                            value={formik.values[`code${curIndex}`]}
                                            // className="form-control b-bottom-field border-top-0 border-start-0 border-end-0 rounded-0 rounded-top"
                                            className={`w-20 h-20 font-black text-2xl text-center border bg-white focus-within:bg-white outlineStyle rounded ${formik.status ? 'border-red-900 focus-within:border-red-900' : 'border-gray focus:border-teal-c-900 focus-within:border-teal-c-900'}`}
                                            maxLength="1"
                                            focus={activeInput === curIndex}
                                            autoFocus
                                            disabled={counter > 2}
                                            onFocus={handleOnFocus(curIndex)}
                                            onChange={(e) => handleOnChange(e.target.value, curIndex)}
                                            onKeyDown={handleOnKeyDown}
                                            onBlur={onBlur}
                                            onPaste={handleOnPaste}

                                        />
                                    })
                            }
                        </div>
                        <div style={{ background: formik?.status || counter > 2 ? 'rgb(235 16 16 / 10%)' : '' }} className="self-center h-12 flex justify-center items-center w-4/5 text-red-900 sm:text-sm text-xs truncate">
                            <p >{formik?.status || " "} {counter > 2 ? 'Your maximum attempts reached for PIN verification please logout' :''}</p>
                        </div>

                        <div className="mt-4 self-center flex flex-row justify-between w-4/5">
                            <button
                                className="flex items-center justify-center w-full rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm bg-bgGreen hover:bg-primary-hover"
                                onClick={() => {
                                    dispatch(props.logout());
                                    props.handleReset();
                                }}
                            >
                                Logout
                            </button>
                        </div>

                    </div>
                </form>
            </div>
        </div>
    );
}

export default injectIntl(
    connect((state) => ({ auth: state.auth }), auth.actions)(OTP)
);
